import { useTranslation } from "react-i18next";
import "./Skeleton.scss";

// Loading placeholders shaped like the real layouts, so content lands where
// the grey blocks were instead of pushing the page around.
//
//   <Skeleton w="60%" h="1rem" />          one block
//   <CardGridSkeleton gridClass="x" />     property-card grid
//   <ListingDetailsSkeleton />             images + info panel
//   <ProfileSkeleton /> <FormSkeleton /> <TableSkeleton /> <PageSkeleton />

export const Skeleton = ({ w, h, radius, circle, className = "", style }) => (
  <span
    className={`skeleton ${circle ? "skeleton--circle" : ""} ${className}`}
    style={{ width: w, height: h, borderRadius: radius, ...style }}
    aria-hidden="true"
  />
);

// Announces "Loading" once to screen readers; the blocks themselves are hidden.
export const SkeletonStatus = ({ as: Tag = "div", className = "", children }) => {
  const [t] = useTranslation("common");
  return (
    <Tag className={className} role="status" aria-busy="true">
      <span className="visually-hidden">{t("a11y.loading")}</span>
      {children}
    </Tag>
  );
};

export const CardSkeleton = ({ as: Tag = "li" }) => (
  <Tag className="skeleton-card" aria-hidden="true">
    <Skeleton className="skeleton-card__media" radius="0" />
    <div className="skeleton-card__body">
      <Skeleton w="45%" h="1.25rem" />
      <Skeleton w="85%" h="0.95rem" />
      <Skeleton w="65%" h="0.85rem" />
      <div className="skeleton-card__specs">
        <Skeleton w="3.5rem" h="0.85rem" />
        <Skeleton w="3.5rem" h="0.85rem" />
        <Skeleton w="4.5rem" h="0.85rem" />
      </div>
      <Skeleton w="35%" h="0.75rem" className="skeleton-card__foot" />
    </div>
  </Tag>
);

// Renders inside the page's own grid class so columns match the loaded state.
export const CardGridSkeleton = ({ count = 6, gridClass = "skeleton-grid" }) => (
  <SkeletonStatus as="ul" className={gridClass}>
    {Array.from({ length: count }, (_, i) => (
      <CardSkeleton key={i} />
    ))}
  </SkeletonStatus>
);

// Title + card grid, for route-level fallbacks before the page itself mounts.
export const CardsPageSkeleton = () => (
  <SkeletonStatus className="skeleton-page skeleton-page--wide skeleton-page--cards">
    <Skeleton w="14rem" h="1.75rem" />
    <Skeleton w="6rem" h="0.9rem" className="skeleton-page__title" />
    <ul className="skeleton-grid">
      {Array.from({ length: 6 }, (_, i) => (
        <CardSkeleton key={i} />
      ))}
    </ul>
  </SkeletonStatus>
);

export const ListingDetailsSkeleton = () => (
  <SkeletonStatus className="skeleton-details">
    <div className="skeleton-details__media">
      <Skeleton className="skeleton-details__hero" radius="0" />
      <div className="skeleton-details__thumbs">
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton key={i} h="4rem" />
        ))}
      </div>
    </div>
    <div className="skeleton-details__panel">
      <Skeleton w="40%" h="1.75rem" />
      <Skeleton w="90%" h="1.1rem" />
      <Skeleton w="70%" h="0.95rem" />
      <div className="skeleton-details__chips">
        <Skeleton w="5rem" h="1.75rem" radius="999px" />
        <Skeleton w="5rem" h="1.75rem" radius="999px" />
        <Skeleton w="6rem" h="1.75rem" radius="999px" />
      </div>
      <Skeleton w="100%" h="2.75rem" radius="10px" />
      <div className="skeleton-details__rows">
        {Array.from({ length: 6 }, (_, i) => (
          <div className="skeleton-details__row" key={i}>
            <Skeleton w="35%" h="0.85rem" />
            <Skeleton w="40%" h="0.85rem" />
          </div>
        ))}
      </div>
      <Skeleton w="100%" h="0.85rem" />
      <Skeleton w="95%" h="0.85rem" />
      <Skeleton w="60%" h="0.85rem" />
    </div>
  </SkeletonStatus>
);

// /user/:username: avatar hero, tabs, info section.
export const ProfileSkeleton = () => (
  <SkeletonStatus className="skeleton-page skeleton-page--profile">
    <div className="skeleton-profile__hero">
      <Skeleton circle className="skeleton-profile__avatar" />
      <Skeleton w="12rem" h="1.6rem" />
      <Skeleton w="7rem" h="0.95rem" />
      <Skeleton w="14rem" h="2.5rem" radius="999px" />
    </div>
    <SectionSkeleton rows={6} />
  </SkeletonStatus>
);

const SectionSkeleton = ({ rows = 4, fields = false }) => (
  <div className="skeleton-section">
    <Skeleton w="30%" h="1.1rem" />
    {Array.from({ length: rows }, (_, i) =>
      fields ? (
        <div className="skeleton-section__field" key={i}>
          <Skeleton w="25%" h="0.8rem" />
          <Skeleton w="100%" h="2.6rem" radius="10px" />
        </div>
      ) : (
        <div className="skeleton-section__row" key={i}>
          <Skeleton w="70%" h="0.85rem" />
          <Skeleton w="55%" h="0.85rem" />
        </div>
      )
    )}
  </div>
);

// Settings and admin edit forms.
export const FormSkeleton = ({ fields = 3, narrow = true }) => (
  <SkeletonStatus
    className={`skeleton-page ${narrow ? "skeleton-page--narrow" : ""}`}
  >
    <Skeleton w="50%" h="1.6rem" className="skeleton-page__title" />
    <SectionSkeleton rows={fields} fields />
    <Skeleton w="9rem" h="2.6rem" radius="10px" className="skeleton-page__btn" />
  </SkeletonStatus>
);

// Admin list pages: toolbar, header row, rows.
export const TableSkeleton = ({ rows = 8, cols = 5 }) => (
  <SkeletonStatus className="skeleton-page skeleton-page--wide">
    <div className="skeleton-table__toolbar">
      <Skeleton w="12rem" h="1.6rem" />
      <Skeleton w="14rem" h="2.4rem" radius="10px" />
    </div>
    <div className="skeleton-table">
      {Array.from({ length: rows + 1 }, (_, r) => (
        <div
          className={`skeleton-table__row ${r === 0 ? "skeleton-table__row--head" : ""}`}
          key={r}
          style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}
        >
          {Array.from({ length: cols }, (_, c) => (
            <Skeleton key={c} w={c === 0 ? "80%" : "60%"} h="0.85rem" />
          ))}
        </div>
      ))}
    </div>
  </SkeletonStatus>
);

// Generic page: title + a couple of content sections.
export const PageSkeleton = () => (
  <SkeletonStatus className="skeleton-page">
    <Skeleton w="40%" h="1.75rem" className="skeleton-page__title" />
    <SectionSkeleton rows={4} />
    <SectionSkeleton rows={3} />
  </SkeletonStatus>
);

export default Skeleton;
